import { Component } from '@angular/core';
import { OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfigService } from '../../services/config.service';
import { UserService } from '../../services/user.service';
import { AlertsService } from '../../services/alerts.service';
import { MetadataService } from '../../services/metadata.service';
import { SearchService } from '../../search/services/search.service';
import { ViewerService } from '../../services/viewer.service';
import { CopyLinkComponent } from '../../components/copy-link/copy-link.component';

import dayjs from 'dayjs';

/**
 * Provides a metadata-only view of a product.
 */
@Component({
	selector: 'doc-metadata-view',
	templateUrl: './doc-metadata-view.component.html',
	styleUrl: './doc-metadata-view.component.css'
})
export class DocMetadataViewComponent implements OnInit {

	@Input() product!: any;
	@Output() triggerCloseMainViewer = new EventEmitter<{ }>();

	LOCALIZATION: any;
	dateFormatDisplay: string = 'MM/DD/YY';
	searchableMetadataFields: any[] = [];

	productInfo: { [key: string]: any } = {};

	constructor(
		private ngbModal: NgbModal,
		private userService: UserService,
		private configService: ConfigService,
		private alertsService: AlertsService,
		private searchService: SearchService,
		private viewerService: ViewerService,
		private metadataService: MetadataService
	) {
		this.LOCALIZATION = this.userService.LOCALIZATION;
	}

	ngOnInit(): void {
		let dateTypeProperty = this.userService.user.properties.awpropertyObjectList.find(function(x: any) { return x.name == "USER_INTERFACE_DATE_TYPE"; });
		this.dateFormatDisplay = dateTypeProperty?.value || 'MM/DD/YY';

		this.searchableMetadataFields = this.configService.getConfig().search.searchableMetadataFields;

		this.loadMetadataRecord();
	}
	
	/**
	 * Reinitialize this component without destroying the DOM.
	 */
	ngOnChanges(changes: SimpleChanges) {
		if (changes['product']) {
			this.loadMetadataRecord();
		}
	}

	/**
	 * Loads the standard KV product metadata.
	 */
	private loadMetadataRecord(): void {
		this.productInfo = {};
		this.searchService.getRecord(this.product.id).then(
			(response: any) => {
				let property = this.userService.user.properties.awpropertyObjectList.find((x: any) => { return x.name === "IS_HIGHLIGHT_VIEW_FIELD" });
				if (property.value === "yes") {
					this.viewerService.getHighlightedMetadataRecord(this.product.id).then(
						(highlights: any) => {
							this.processMetadata(response, highlights);
						},
						(err: any) => {
							console.error("Failed to load product metadata:", err);
							this.alertsService.error({
								message: "Failed to retrieve metadata for this document."
							});
						}
					);
				} else {
					this.processMetadata(response);
				}
			},
			(err: any) => {
				console.error("Failed to load product metadata:", err);
				this.alertsService.error({
					message: "Failed to retrieve metadata for this document."
				});
			}
		);
	}

	private processMetadata(result: any, highlights?: any) {
		let hl: { [key: string]: any } = {};
		if (highlights && highlights["aw:record"]["print"]) {
			hl = highlights["aw:record"]["print"];
		}

		let productHash = result["csw:GetRecordByIdResponse"].iStoreRecord;
		let productInfo: { [key: string]: any } = {};
		let validFields = this.userService.user.properties.awfieldObjectList.filter((x: any) => { return x.isAvailable && x.isMetadata });

		for (let p = 0; p < validFields.length; p++) {
			let field = validFields[p];

			if (!productHash[field.name]) {
				continue;
			}

			productInfo[field.displayName] = {
				label: field.displayName,
				value: hl[field.name] ? hl[field.name] : productHash[field.name],
				maxLength: (field.isLink ? -1 : 220),
				links: null,
				name: field.name
			};

			productInfo[field.displayName].cleanedValue = this.metadataService.cleanValue(productInfo[field.displayName].value);
			productInfo[field.displayName].isValidCollection =
				this.metadataService.isValidFieldCollectionValue(productInfo[field.displayName].name, productInfo[field.displayName].cleanedValue);

			if (field.type == "DATE") {
				productInfo[field.displayName].value = dayjs(productInfo[field.displayName].value).format(this.dateFormatDisplay);
			}

			if (field.name == "requires") {
				productInfo[field.displayName].warning = true;
			}

			if (field.isLink) {
				productInfo[field.displayName].links = this.metadataService.linksFromString(productHash[field.name], field.name);
			} else {
				if (productInfo[field.displayName].value) {
					productInfo[field.displayName].value = productInfo[field.displayName].value.replace(/; /g, ';').replace(/;/g, '; ');
				}
			}
		}

		this.productInfo = productInfo;
	}

	/**
	 * Executes a search using the field and value.
	 */
	resultFieldSearch(field: string, value: string): void {
		console.log(`Searching for: ${field}, with value: ${value}`);

		let payload = {
			field: field,
			value: value
		};
		this.searchService.broadcastSearchResultFieldEvent(payload);
		this.triggerCloseMainViewer.emit({ });
	}

	displayLinkMsg(link: string): void {
		if (link.startsWith("file://")) {
			const modalRef = this.ngbModal.open(CopyLinkComponent);
			modalRef.componentInstance.link = link;

			modalRef.result.then(
				(payload) => {
					console.log('Modal dismissed');
				},
				(reason) => {
					console.log('Modal dismissed');
				}
			);
		}
	}
}
